import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Car, DollarSign, CreditCard, ArrowLeft, Loader2, CheckCircle, Info } from "lucide-react";
import { calculateDeal, submitCustomerDeal, DealMathResult, DealMathInputs } from "@/Calls/callDeals";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { socket } from "@/lib/socket";

const TERMS = [24, 36, 48, 60, 72, 84];

function fmt(n: number | undefined | null) {
    if (!n && n !== 0) return "—";
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtMo(n: number) {
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`;
}

export default function CustomerDealBuilderPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Vehicle from query params (passed from marketplace/vehicle detail)
    const vehicleId = params.get("vehicleId") ?? "";
    const dealerId = params.get("dealerId") ?? "";
    const dealerRoom = params.get("dealerRoom") ?? "";
    const vehiclePrice = Number(params.get("price") ?? 0);
    const year = params.get("year") ?? "";
    const make = params.get("make") ?? "";
    const model = params.get("model") ?? "";
    const trim = params.get("trim") ?? "";
    const vin = params.get("vin") ?? "";
    const color = params.get("color") ?? "";
    const mileage = params.get("mileage") ?? "";

    const [dealType, setDealType] = useState<"FINANCE" | "CASH">("FINANCE");
    const [form, setForm] = useState({
        apr: "7.99",
        termMonths: "60",
        downPayment: "",
        tradeAllowance: "",
        tradePayoff: "",
        salesTaxRate: "0.07",
        titleLicenseFee: "250",
        docFee: "499",
        warranty: "",
        gap: "",
    });

    const [math, setMath] = useState<DealMathResult | null>(null);
    const [calculating, setCalculating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submittedDealId, setSubmittedDealId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setForm(p => ({ ...p, [k]: e.target.value }));

    const buildInputs = useCallback((): DealMathInputs => ({
        vehiclePrice,
        apr: Number(form.apr ?? 0),
        termMonths: Number(form.termMonths ?? 60),
        downPayment: Number(form.downPayment ?? 0),
        tradeAllowance: Number(form.tradeAllowance ?? 0),
        tradePayoff: Number(form.tradePayoff ?? 0),
        salesTaxRate: Number(form.salesTaxRate ?? 0),
        titleLicenseFee: Number(form.titleLicenseFee ?? 0),
        docFee: Number(form.docFee ?? 0),
        warranty: Number(form.warranty ?? 0),
        gap: Number(form.gap ?? 0),
        dealType,
    }), [form, dealType, vehiclePrice]);

    // Recalculate on form change with debounce
    useEffect(() => {
        if (!vehiclePrice) return;
        const t = setTimeout(async () => {
            setCalculating(true);
            try {
                const result = await calculateDeal(buildInputs());
                setMath(result);
            } catch { /* server may be unreachable in dev */ } finally {
                setCalculating(false);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [form, dealType, vehiclePrice, buildInputs]);

    // Register socket for real-time deal updates from dealer
    useEffect(() => {
        if (user) {
            socket.emit("registerCustomer", { customerId: user.id });
        }
    }, [user]);

    const handleSubmit = async () => {
        if (!vehiclePrice || !dealerId) {
            setError("Vehicle or dealer information is missing.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await submitCustomerDeal({
                dealerId: Number(dealerId),
                vehicleId: vehicleId ? Number(vehicleId) : undefined,
                dealType,
                vin, year, make, model, trim, color, mileage,
                vehiclePrice,
                ...buildInputs(),
                dealerRoom,
            });
            setSubmittedDealId(result.deal?.deal_id ?? null);
            setSubmitted(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        const nextSteps = [
            {
                num: 1,
                label: "Complete Your Profile",
                desc: "Add your address, employment, and income — speeds up lender decisions.",
                to: "/customer/profile",
                cta: "Complete Profile",
                done: false,
            },
            ...(dealType === "FINANCE" ? [{
                num: 2,
                label: "Submit a Credit Application",
                desc: "Pre-filled from your profile. Takes about 3 minutes.",
                to: submittedDealId ? `/customer/credit-app?dealId=${submittedDealId}` : "/customer/credit-app",
                cta: "Apply for Financing",
                done: false,
            }] : []),
            {
                num: dealType === "FINANCE" ? 3 : 2,
                label: "Await Dealer Decision",
                desc: "The dealer will review and respond. You'll see updates in real time on My Deals.",
                to: "/customer/deals",
                cta: "Watch My Deals",
                done: false,
            },
            {
                num: dealType === "FINANCE" ? 4 : 3,
                label: "Download Your Paperwork",
                desc: "Once the deal is finalized you can download your Buyer's Order PDF.",
                to: "/customer/documents",
                cta: "My Documents",
                done: false,
            },
        ];

        return (
            <div className="max-w-xl mx-auto mt-10 space-y-8 px-4">
                <div className="text-center space-y-3">
                    <div className="rounded-full bg-emerald-100 p-4 w-16 h-16 mx-auto flex items-center justify-center">
                        <CheckCircle className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">Deal Submitted!</h1>
                    <p className="text-muted-foreground text-sm">
                        Your deal for the <strong>{year} {make} {model}</strong> has been sent to the dealer.
                        Complete the steps below to finish your purchase online.
                    </p>
                </div>

                <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">What happens next</p>
                    {nextSteps.map((step) => (
                        <div key={step.num} className="flex items-start gap-4 p-4 rounded-xl border bg-card">
                            <div className="rounded-full bg-indigo-100 dark:bg-indigo-950/40 text-indigo-600 font-bold text-sm w-8 h-8 flex items-center justify-center shrink-0">
                                {step.num}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{step.label}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                            </div>
                            <Link to={step.to} className="shrink-0">
                                <Button size="sm" variant="outline" className="text-xs">
                                    {step.cta}
                                </Button>
                            </Link>
                        </div>
                    ))}
                </div>

                <div className="flex gap-3 justify-center pb-8">
                    <Link to="/customer/dashboard">
                        <Button variant="outline">Dashboard</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
                <button onClick={() => navigate(-1)}>
                    <Button variant="ghost" size="sm">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                </button>
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">Build Your Deal</h1>
                    <p className="text-muted-foreground text-sm">
                        {year} {make} {model} {trim}
                        {vehiclePrice ? ` · ${fmt(vehiclePrice)}` : ""}
                    </p>
                </div>
            </div>

            <div className="grid lg:grid-cols-[1fr_340px] gap-6">
                {/* Left — inputs */}
                <div className="space-y-5">
                    {/* Deal type toggle */}
                    <Card className="p-5">
                        <h2 className="text-xs font-medium uppercase tracking-wide mb-4">How would you like to pay?</h2>
                        <div className="grid grid-cols-2 gap-3">
                            {(["FINANCE", "CASH"] as const).map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => setDealType(t)}
                                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                                        dealType === t
                                            ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30"
                                            : "border-border hover:border-indigo-300"
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        {t === "FINANCE" ? <CreditCard className="h-4 w-4 text-indigo-500" /> : <DollarSign className="h-4 w-4 text-emerald-500" />}
                                        <span className="font-medium text-sm">{t === "FINANCE" ? "Finance" : "Cash"}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {t === "FINANCE" ? "Monthly payments with a loan" : "Pay the full amount today"}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </Card>

                    {/* Finance options */}
                    {dealType === "FINANCE" && (
                        <Card className="p-5 space-y-4">
                            <h2 className="text-xs font-medium uppercase tracking-wide">Finance Terms</h2>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="mb-1 block text-xs font-medium uppercase">APR (%)</label>
                                    <Input type="number" value={form.apr} onChange={set("apr")} step="0.01" min={0} max={30} placeholder="7.99" />
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs font-medium uppercase">Term (months)</label>
                                    <select
                                        value={form.termMonths}
                                        onChange={set("termMonths")}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    >
                                        {TERMS.map(t => <option key={t} value={t}>{t} months</option>)}
                                    </select>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Down payment & Trade */}
                    <Card className="p-5 space-y-4">
                        <h2 className="text-xs font-medium uppercase tracking-wide">Down Payment & Trade-In</h2>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Down Payment ($)</label>
                                <Input type="number" value={form.downPayment} onChange={set("downPayment")} placeholder="0" min={0} />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Trade-In Value ($)</label>
                                <Input type="number" value={form.tradeAllowance} onChange={set("tradeAllowance")} placeholder="0" min={0} />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Trade Payoff ($)</label>
                                <Input type="number" value={form.tradePayoff} onChange={set("tradePayoff")} placeholder="0" min={0} />
                            </div>
                        </div>
                    </Card>

                    {/* Add-ons */}
                    <Card className="p-5 space-y-4">
                        <h2 className="text-xs font-medium uppercase tracking-wide">Protection & Add-ons</h2>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Extended Warranty ($)</label>
                                <Input type="number" value={form.warranty} onChange={set("warranty")} placeholder="0" min={0} />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">GAP Insurance ($)</label>
                                <Input type="number" value={form.gap} onChange={set("gap")} placeholder="0" min={0} />
                            </div>
                        </div>
                    </Card>

                    {/* Taxes & Fees */}
                    <Card className="p-5 space-y-4">
                        <h2 className="text-xs font-medium uppercase tracking-wide">Taxes & Fees</h2>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Sales Tax Rate</label>
                                <Input type="number" value={form.salesTaxRate} onChange={set("salesTaxRate")} step="0.001" min={0} max={0.2} placeholder="0.07" />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Title & License ($)</label>
                                <Input type="number" value={form.titleLicenseFee} onChange={set("titleLicenseFee")} min={0} />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Doc Fee ($)</label>
                                <Input type="number" value={form.docFee} onChange={set("docFee")} min={0} />
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Right — live summary */}
                <div className="lg:sticky lg:top-8 h-fit space-y-4">
                    <Card className="p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xs font-medium uppercase tracking-wide">Deal Summary</h2>
                            {calculating && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                        </div>

                        {math ? (
                            <>
                                {dealType === "FINANCE" && (
                                    <div className="text-center py-3 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800">
                                        <p className="text-3xl font-semibold text-indigo-600">{fmtMo(math.monthlyPayment)}</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {form.termMonths} months @ {form.apr}% APR
                                        </p>
                                    </div>
                                )}

                                <div className="space-y-2 text-sm">
                                    <Row label="Vehicle Price" value={fmt(vehiclePrice)} />
                                    {math.totalAddOns > 0 && <Row label="Add-ons" value={fmt(math.totalAddOns)} />}
                                    <Row label="Sales Tax" value={fmt(math.salesTax)} />
                                    <Row label="Fees" value={fmt(math.totalFees)} />
                                    {math.netTrade !== 0 && (
                                        <Row label="Net Trade" value={fmt(Math.abs(math.netTrade))} sign={math.netTrade > 0 ? "-" : "+"} />
                                    )}
                                    {Number(form.downPayment) > 0 && (
                                        <Row label="Down Payment" value={fmt(Number(form.downPayment))} sign="-" />
                                    )}
                                    <div className="border-t pt-2 font-medium">
                                        <Row label={dealType === "FINANCE" ? "Amount Financed" : "Cash Due"} value={fmt(dealType === "FINANCE" ? math.amountFinanced : math.cashPrice)} bold />
                                    </div>
                                    {dealType === "FINANCE" && (
                                        <>
                                            <Row label="Total of Payments" value={fmt(math.totalOfPayments)} />
                                            <Row label="Finance Charge" value={fmt(math.totalFinanceCharge)} />
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <Car className="h-8 w-8 mx-auto mb-2 opacity-40" />
                                <p className="text-sm">Calculating…</p>
                            </div>
                        )}
                    </Card>

                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                    <Button
                        onClick={handleSubmit}
                        disabled={submitting || !math}
                        className="w-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white py-6 text-base"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Submitting Deal…
                            </>
                        ) : (
                            "Submit Deal to Dealer"
                        )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                        <Info className="h-3 w-3" />
                        No credit impact — dealer will contact you to finalize
                    </p>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, sign, bold }: { label: string; value: string; sign?: string; bold?: boolean }) {
    return (
        <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}>
            <span className="text-muted-foreground">{label}</span>
            <span>{sign && <span className="text-muted-foreground mr-1">{sign}</span>}{value}</span>
        </div>
    );
}
